<script setup lang="ts">
import { formatCurrency } from '@/globals/number.ts'
import LabelBase from '@/components/label/LabelBase.vue'
import type { Part } from '@/types/model'
import { computed, reactive, watch } from 'vue'
import { getUrlImage } from '@/globals/app.ts'
import type { StockWarehouse } from '@/helpers/part.helper'
import InputBase from '../input/InputBase.vue'
import AnimationShow from '../animation/AnimationShow.vue'

// props and emits
const props = withDefaults(
  defineProps<{
    part: Part | null
    detail?: boolean
    stock?: StockWarehouse | null
  }>(),
  {
    detail: false,
    stock: null,
  },
)

// refs
const formStock = reactive({
  ez: props.part?.stock ?? 0,
  crb: props.stock?.crb ?? 0,
  cpc: props.stock?.cpc ?? 0,
})

// computed
const coverImg = computed<string>(() => {
  if (props.part && props.part.covers)
    return getUrlImage(
      props.part.covers?.from ?? 'parts',
      (props.part.covers?.name ?? 'default.png') + '?w=150',
    )
  return getUrlImage('parts', 'default.png')
})
const withStock = computed(() => props.stock !== null && props.stock !== undefined)

watch(
  () => props.stock,
  (newVal) => {
    formStock.cpc = newVal?.cpc ?? 0
    formStock.crb = newVal?.crb ?? 0
  },
)
</script>

<template>
  <div v-if="part" class="flex gap-4">
    <div>
      <img
        :src="coverImg"
        alt="part image"
        class="min-w-17 w-17 h-17 rounded-lg object-cover object-center"
      />
    </div>
    <div>
      <label-base size="sm" color="stone" class="block font-mono">{{ part.sku }} </label-base>
      <label-base class="block font-semibold mt-0.5">{{ part.name }}</label-base>
      <label-base size="sm" class="block font-mono"
        >{{ formatCurrency(part.price ?? 0) }}
      </label-base>
      <label-base
        v-if="detail && (part.price_sell ?? 0) > (part.price ?? 0)"
        color="blue"
        size="sm"
        class="block font-mono"
        >{{ formatCurrency(part.price_sell ?? 0) }}
      </label-base>
    </div>
    <div v-if="detail" class="ml-auto px-2 border-l border-stone-500 border-dashed text-center">
      <label-base color="stone" size="xs" class="block">Stock</label-base>
      <label-base class="block font-mono mt-1" size="xl">{{ part.stock }}</label-base>
    </div>
  </div>

  <label-base v-else size="sm" color="stone">Tidak ada part</label-base>

  <animation-show :show="withStock" custom-class="mt-2">
    <div class="grid grid-cols-3 gap-2">
      <div class="w-full">
        <input-base
          v-model="formStock.ez"
          name="ez"
          type="number"
          placeholder="Stock erwinzilla"
          label="Erwinzilla"
          class="font-mono"
          :disabled="formStock.ez <= 0"
        ></input-base>
      </div>

      <div class="w-full">
        <input-base
          v-model="formStock.crb"
          name="crb"
          type="number"
          placeholder="Stock Cirebon"
          label="Cirebon"
          class="font-mono"
          :disabled="formStock.crb <= 0"
        ></input-base>
      </div>

      <div class="w-full">
        <input-base
          v-model="formStock.cpc"
          name="cpc"
          type="number"
          placeholder="Stock CPC"
          label="CPC"
          class="font-mono"
          :disabled="formStock.cpc <= 0"
        ></input-base>
      </div>
    </div>
  </animation-show>
</template>

<style scoped></style>
